import type { AuditStatesHiddenReason, JobRunStatus } from '@/shared/api/resources';

/**
 * Rotulos da tela de logs (P-LOG): eventos de acesso, motivos, situacao das rotinas
 * e tipos de entidade da trilha.
 *
 * O servidor devolve as chaves gravadas, como `auth.login.failed`; a tela traduz.
 * Chave que nao esta aqui aparece crua, para que um evento novo nao suma da lista.
 */

/** Eventos de acesso gravados em `security_events`. */
export const rotuloDoEvento: Record<string, string> = {
  'auth.login.succeeded': 'Entrada na plataforma',
  'auth.login.failed': 'Falha de entrada',
  'auth.logout': 'Saida',
  'auth.token.rotated': 'Renovacao da sessao',
  'auth.refresh.reuse_detected': 'Reuso de sessao detectado',
  'auth.sessions.revoked': 'Sessoes encerradas',
  'auth.tenant.switch': 'Troca de empresa',
  'auth.password_recovery.requested': 'Pedido de troca de senha',
  'auth.password.reset': 'Senha redefinida pelo link',
  'auth.password.reset_rejected': 'Redefinicao de senha recusada',
  'auth.password.changed': 'Senha trocada',
  'auth.password.change_rejected': 'Troca de senha recusada',
  'platform.bootstrap.first_admin_created': 'Primeiro administrador criado',
};

export function eventoLegivel(acao: string): string {
  return rotuloDoEvento[acao] ?? acao;
}

/** Opcoes do filtro "Evento": o comeco da acao, como o servidor filtra. */
export const opcoesDoEvento = [
  { value: 'auth.login', label: 'Entradas (sucesso e falha)' },
  { value: 'auth.login.succeeded', label: 'Entrada na plataforma' },
  { value: 'auth.logout', label: 'Saida' },
  { value: 'auth.token.rotated', label: 'Renovacao da sessao' },
  { value: 'auth.tenant.switch', label: 'Troca de empresa' },
  { value: 'auth.password', label: 'Troca e redefinicao de senha' },
  { value: 'auth.password_recovery', label: 'Pedido de troca de senha' },
  { value: 'auth.refresh', label: 'Reuso de sessao detectado' },
];

/** Motivos gravados nos detalhes do evento. */
const rotuloDoMotivo: Record<string, string> = {
  invalid_password: 'senha incorreta',
  too_many_attempts: 'tentativas demais',
  unknown_account: 'conta desconhecida',
  account_not_authenticable: 'conta sem acesso',
  tenant_inactive: 'empresa inativa',
  no_active_membership: 'sem vinculo ativo com a empresa',
  too_many_requests: 'pedidos demais',
  account_not_eligible: 'conta sem direito a troca',
  issued: 'link enviado',
  issued_by_admin: 'link emitido pela administracao',
  current_password_mismatch: 'senha atual incorreta',
};

/** Detalhe do evento em texto: o motivo e, quando houver, as sessoes encerradas. */
export function detalheDoEvento(detalhes: Record<string, unknown>): string {
  const partes: string[] = [];
  const motivo = detalhes.reason;
  if (typeof motivo === 'string' && motivo)
    partes.push(rotuloDoMotivo[motivo] ?? motivo);
  if (detalhes.sessions_revoked === true) partes.push('sessoes encerradas');
  const tentativas = detalhes.failed_attempts;
  if (typeof tentativas === 'number') partes.push(`${tentativas} tentativas`);
  return partes.join('; ');
}

export const rotuloDaSituacao: Record<
  JobRunStatus,
  { label: string; tone: 'neutral' | 'info' | 'success' | 'danger' }
> = {
  running: { label: 'Em andamento', tone: 'info' },
  succeeded: { label: 'Concluida', tone: 'success' },
  failed: { label: 'Com falha', tone: 'danger' },
  skipped: { label: 'Ignorada', tone: 'neutral' },
};

export const opcoesDaSituacao = (Object.keys(rotuloDaSituacao) as JobRunStatus[]).map(
  (situacao) => ({ value: situacao, label: rotuloDaSituacao[situacao].label }),
);

/**
 * Tipos de entidade mais lidos na trilha, para o filtro "Tipo de entidade". O tipo
 * fora da lista continua na trilha e aparece cru na coluna.
 */
export const rotuloDaEntidade: Record<string, string> = {
  operation: 'Operacao (cotacao)',
  policyholder: 'Tomador',
  insured: 'Segurado',
  broker: 'Corretor',
  policy: 'Apolice',
  endorsement: 'Endosso',
  product: 'Produto',
  modality: 'Modalidade',
  modality_group: 'Grupo de modalidades',
  document: 'Documento',
  document_template: 'Template',
  generated_document: 'Documento gerado',
  credit_analysis: 'Analise de credito',
  credit_parameter_set: 'Parametros de credito',
  underwriting_review: 'Subscricao',
  commission: 'Comissao',
  claim: 'Sinistro',
  tenant_member: 'Usuario',
  member_invitation: 'Convite',
  role: 'Perfil de acesso',
  tenant: 'Empresa',
  tenant_settings: 'Configuracoes da empresa',
  decline_reason: 'Motivo padronizado',
  tenant_integration: 'Integracao',
};

export function entidadeLegivel(tipo: string): string {
  return rotuloDaEntidade[tipo] ?? tipo;
}

export const opcoesDaEntidade = Object.entries(rotuloDaEntidade)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label));

/** Por que o conteudo de uma alteracao nao aparece. */
export function motivoDoConteudoOculto(motivo: AuditStatesHiddenReason): string {
  if (motivo === 'entidade') {
    return 'O seu perfil nao le este cadastro, e o conteudo da alteracao fica oculto.';
  }
  return 'O seu perfil nao inclui o conteudo das alteracoes (auditoria.trilha.view).';
}
